<script setup lang="ts">
import { computed, ref } from 'vue';
import { COUNTRY_CODE } from '@/utils/constants';
import IdealPostcodesAutocomplete from '../IdealPostcodesAutocomplete.vue';
import type {
  ConnectionFormData,
  ConnectionFormDataPerGroup,
  ConnectionFormDataPerPhase,
} from '../../models/Connection';
import type { AddressSelectedPayload } from '../../types/ideal-postcodes';
import { PHASE } from '../../utils/constants';
import { useDeviceSpecificationStore } from '../../stores/deviceSpecificationStore';
import { createUniquenessValidator } from '../../utils/output-connections';
import { getPhaseColor } from '../../constants/phase-color';
import type { FormItemInstance, Rule } from 'ant-design-vue/es/form';

const props = defineProps<{
  groupNumber: number;
  formDataIndex: number;
  formData: ConnectionFormData;
  connectionGroup: ConnectionFormDataPerGroup;
  placeholder: string;
}>();

const emit = defineEmits<{
  (e: 'updateTripleConnectionName', value: ConnectionFormDataPerPhase): void;
}>();

const formItemRef = ref<FormItemInstance | null>(null);
const connectionName = computed({
  get() {
    const allPhaseMatchingName =
      props.connectionGroup.L1.name === props.connectionGroup.L2.name &&
      props.connectionGroup.L1.name === props.connectionGroup.L3.name;

    return allPhaseMatchingName ? props.connectionGroup.L1.name : '';
  },
  set(name: string) {
    emit('updateTripleConnectionName', {
      ...props.connectionGroup.L1,
      name,
      externalPropertyId: null,
      externalPropertyIdSource: null,
    });
  },
});

const handleIdpcAddressSelected = async ({ line1, uprn }: AddressSelectedPayload) => {
  emit('updateTripleConnectionName', {
    ...props.connectionGroup.L1,
    name: line1,
    externalPropertyId: uprn || null,
    externalPropertyIdSource: uprn ? 'uprn' : null,
  });
  formItemRef.value?.onFieldChange();
};

const oneTripleNameRequired = async () => {
  // Assume if the names is different, it's because the user hasn't entered a name.
  const { L1, L2, L3 } = props.connectionGroup;
  if (L1.name !== L2.name || L2.name !== L3.name) {
    return Promise.reject('Connection name is required');
  }
};

const deviceSpecificationStore = useDeviceSpecificationStore();
const country = computed(() => deviceSpecificationStore.solshareSite?.country as COUNTRY_CODE);
const postcode = computed(() => deviceSpecificationStore.solshareSite?.sitePostcode);
const useIdealPostcodes = computed(() => country.value === COUNTRY_CODE['United Kingdom']);
const sharedInputAttrs = computed(() => ({
  size: 'large' as const,
  class: ['name-input', 'name-input__onetriple'],
  placeholder: props.placeholder,
  'data-tn': `name-input-onetriple-${props.groupNumber}`,
}));

const validationRules: Rule[] = [
  { validator: oneTripleNameRequired },
  { validator: createUniquenessValidator(props.formData, props.formDataIndex, PHASE.L1) },
];
</script>

<template>
  <a-form-item
    :key="`connection-name-onetriple-${groupNumber}`"
    ref="formItemRef"
    :name="[formDataIndex, 'connectionGroup', 'L1', 'name']"
    :rules="validationRules"
    class="form-item__connection-name"
  >
    <div class="input-wrapper">
      <div class="labels-column">
        <a-row>
          <a-col
            flex="none"
            class="phase-color-container"
            :style="{ backgroundColor: getPhaseColor(country, PHASE.L1) }"
          ></a-col>
          <a-col flex="auto">
            <span class="name-input name-input__L1">
              <span class="ant-input-group-addon">L1-{{ groupNumber }}</span>
            </span>
          </a-col>
        </a-row>
        <a-row>
          <a-col
            flex="none"
            class="phase-color-container"
            :style="{ backgroundColor: getPhaseColor(country, PHASE.L2) }"
          ></a-col>
          <a-col flex="auto">
            <span class="name-input name-input__L2">
              <span class="ant-input-group-addon">L2-{{ groupNumber }}</span>
            </span>
          </a-col>
        </a-row>
        <a-row>
          <a-col
            flex="none"
            class="phase-color-container"
            :style="{ backgroundColor: getPhaseColor(country, PHASE.L3) }"
          ></a-col>
          <a-col flex="auto">
            <span class="name-input name-input__L3">
              <span class="ant-input-group-addon">L3-{{ groupNumber }}</span>
            </span>
          </a-col>
        </a-row>
      </div>
      <template v-if="useIdealPostcodes">
        <IdealPostcodesAutocomplete
          v-model:value="connectionName"
          :inputFieldId="`idpc-autocomplete-input-onetriple-${groupNumber}`"
          :projectCountry="country"
          :postcode="postcode"
          v-bind="sharedInputAttrs"
          @address-selected="handleIdpcAddressSelected"
        />
      </template>
      <template v-else>
        <a-input v-model:value="connectionName" v-bind="sharedInputAttrs" />
      </template>
    </div>
  </a-form-item>
</template>

<style scoped lang="less">
.form-item__connection-name {
  width: 100%;
  flex-grow: 1;
  margin-bottom: 0;
}

.input-wrapper {
  display: flex;
  gap: 0;
  padding-right: @content-side-margin;
}

.labels-column {
  display: flex;
  flex-direction: column;
  gap: 0;

  .phase-color-container {
    width: @phase-color-width;
  }

  .ant-input-group-addon {
    align-items: center;
    justify-content: center;
    text-align: center;
    display: flex;
    min-height: 2.8rem;
    width: auto;
  }
}

.name-input.name-input__onetriple {
  display: flex;
  flex: 1;
  width: 100%;

  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
  border-color: @color-primary;
}
</style>
